import { useCallback, useMemo, useState } from "react";
import { styled, css } from "styled-components";
import { useDropzone } from "react-dropzone-esm";
import { mobileBreakPointCSS } from "../../defaults/theme.js";
import {
  upload as uploadIcon,
  destroy,
  file as fileIcon,
} from "../../icons/index.js";
import { FILE_ENDPOINT } from "../../util/constants.js";
import { Spinner } from "../Spinner.js";

type FileUploadStatus = "success" | "failed";

interface FileServiceData {
  ETag: string;
  Location: string;
  key: string;
  Key: string;
  Bucket: string;
}

interface FileServiceResponseInterface {
  status: FileUploadStatus;
  message: string;
  data: FileServiceData;
}

const Wrapper = styled.div<{ $isDragActive: boolean; $uploading: boolean }>`
  padding: 0 ${(props) => props.theme.spacing.xs};
  margin-top: ${(props) => props.theme.spacing.xs};
  background-color: var(--swatch3);
  color: var(--swatch2);
  border: 1px dashed var(--swatch2);
  border-radius: 5px;
  outline: none;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  height: 70px;
  input {
    opacity: 0.001;
    position: absolute;
    bottom: 0;
  }
  ${(props) =>
    props.$isDragActive &&
    css`
      border-color: var(--swatch1);
    `};
  ${(props) =>
    props.$uploading &&
    css`
      pointer-events: none;
    `};

  @media (min-width: ${mobileBreakPointCSS}) {
    padding: 0 ${(props) => props.theme.spacing.xs};
    margin-top: ${(props) => props.theme.spacing.xs};
  }
`;

const Upload = styled.div`
  display: flex;
  align-items: center;
  padding: ${(props) => props.theme.spacing.xs} 0;

  @media (min-width: ${mobileBreakPointCSS}) {
    padding: ${(props) => props.theme.spacing.s} 0;
  }
`;

const Label = styled.label`
  display: block;
  margin-bottom: ${(props) => props.theme.spacing.xs};
  font-weight: 600;
`;

const Icon = styled.div`
  margin-right: ${(props) => props.theme.spacing.xs};
  display: flex;
  justify-content: center;
  align-items: center;
  svg {
    width: 38px;
    height: 38px;
  }

  @media (min-width: ${mobileBreakPointCSS}) {
    margin-right: ${(props) => props.theme.spacing.xs};
  }
`;

const Text = styled.p`
  margin: 0;
`;

const Preview = styled.div`
  display: flex;
  align-items: center;
  padding: ${(props) => props.theme.spacing.xs} 0;
  flex-wrap: wrap;
  svg {
    display: block;
    width: 38px;
    height: 38px;
    margin-right: ${(props) => props.theme.spacing.xs};
  }

  @media (min-width: ${mobileBreakPointCSS}) {
    padding: ${(props) => props.theme.spacing.s} 0;
    svg {
      margin-right: ${(props) => props.theme.spacing.xs};
    }
  }
`;

const LoadingOverlay = styled.div.attrs({ className: "b3" })`
  position: absolute;
  background: white;
  height: 100%;
  width: 100%;
  left: 0;
  top: 0;
  opacity: 0.9;
  display: flex;
  justify-content: center;
  align-items: center;

  div {
    margin-left: ${(props) => props.theme.spacing.s};
  }

  span {
    display: none;

    @media (min-width: ${mobileBreakPointCSS}) {
      display: inline-block;
    }
  }
`;

const Delete = styled.a`
  display: block;
  text-decoration: none;
  margin-left: auto;
  margin-top: ${(props) => props.theme.spacing.xs};
  color: var(--error);
  display: flex;
  align-items: center;
  justify-content: flex-end;
  width: 100%;
  svg {
    width: 20px;
    height: 20px;
    margin-left: ${(props) => props.theme.spacing.xs};
  }

  @media (min-width: ${mobileBreakPointCSS}) {
    width: auto;
    margin-left: auto;
    justify-content: flex-start;
    margin-top: 0;
    svg {
      margin-left: ${(props) => props.theme.spacing.xs};
    }
  }
`;

const Error = styled.div`
  margin-top: ${(props) => props.theme.spacing.xs};
  color: var(--error);
`;

interface Props {
  id: string;
  onUpload: (file: FileServiceData) => any;
  onDelete: () => any;
  required?: boolean;
  name?: string;
  label?: string;
}

export const Uploader: React.FunctionComponent<Props> = ({
  id,
  onUpload,
  onDelete,
  required,
  label,
  name,
}) => {
  const [uploading, setUploading] = useState(false);
  const [files, setFiles] = useState([] as File[]);
  const [isInvalid, setIsInvalid] = useState(false);
  const [error, setError] = useState(false);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        setFiles(acceptedFiles);
        upload(acceptedFiles[0]);
      }
    },
    [onUpload]
  );

  //@ts-ignore
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    maxFiles: 1,
    multiple: false,
  });

  const file: File | undefined = useMemo(() => {
    if (files.length > 0) {
      const file = files[0];
      return file;
    }

    return undefined;
  }, [files.length]);

  const upload = useCallback(
    async (file: File) => {
      setUploading(true);

      const formData = new FormData();
      formData.append("file", file);

      const response = await fetch(`${FILE_ENDPOINT}/files`, {
        method: "POST",
        body: formData,
      });

      const media: FileServiceResponseInterface = await response.json();

      setUploading(false);

      if (media && media.status === "success") {
        onUpload(media.data);
        setIsInvalid(false);
        setError(false);
      } else {
        setError(true);
      }
    },
    [onUpload]
  );

  const handleDelete = useCallback(() => {
    setFiles([]);
    onDelete();
  }, [onDelete]);

  return (
    <>
      {label && <Label htmlFor={id}>{label}</Label>}
      <Wrapper
        {...getRootProps()}
        $isDragActive={isDragActive}
        $uploading={uploading}
      >
        {uploading && (
          <LoadingOverlay>
            <span>Vent mens vi laster opp filen din</span> <Spinner />
          </LoadingOverlay>
        )}
        {file ? (
          <Preview>
            {fileIcon}
            <Text>{file.name}</Text>
            <Delete
              href="#"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleDelete();
              }}
            >
              Slett {destroy}
            </Delete>
          </Preview>
        ) : (
          <Upload>
            <Icon>{uploadIcon}</Icon>
            <Text>
              {isDragActive
                ? "Slipp filen din her."
                : "Dra og slipp fil her, eller klikk for å laste opp"}
            </Text>
          </Upload>
        )}
        <input
          key={files.length}
          id={id}
          name={name}
          multiple={false}
          disabled={uploading}
          required={required && !file}
          onInvalid={() => {
            setIsInvalid(true);
          }}
          {...getInputProps({
            style: {},
          })}
        />
      </Wrapper>
      {isInvalid && <Error>Du må velge fil før du kan gå videre.</Error>}
      {error && (
        <Error>
          Noe gikk galt med opplastingen. Oppdater nettleservinduet og prøv på
          nytt.
        </Error>
      )}
    </>
  );
};
